import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, CheckCircle, Leaf, ShieldCheck, FlaskConical, Sparkles, Mail, Tag, Beaker, HeartPulse, Brain, Activity, Minus, Plus, ShoppingBag, BellRing, FileText } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import SEO from '../components/SEO';
import ImageLightbox from '../components/ImageLightbox';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const SOMIN_ID = '7a3e9c14-2b6d-4f8a-9c1e-5d0b7f2a4e61';

const DEFAULT_GALLERY = ['/products/somin-on-1.jpg', '/products/somin-on-2.jpg', '/products/somin-on-3.jpg', '/products/somin-on-4.jpg', '/products/somin-on-5.jpg'];

const SUPPLEMENT_FACTS = [
  { name: 'Somin-On™ (Ashwagandha extract)', amount: 'NLT 125 mg', nrv: '†' },
  { name: 'Turmeric Extract (BCM-95®)', amount: 'NLT 100 mg', nrv: '†' },
  { name: 'Magnesium (as bisglycinate)', amount: 'NLT 75 mg', nrv: '20%' },
  { name: 'Vitamin B6', amount: 'NLT 0.63 mg', nrv: '45%' },
  { name: 'Folic Acid (Vitamin B9)', amount: 'NLT 0.50 mg', nrv: '250%' },
  { name: 'Vitamin B12', amount: 'NLT 1.15 µg', nrv: '46%' },
];

const KEY_BENEFITS = [
  { icon: Sparkles, title: 'Advanced Nutritional Support', text: 'A multi-nutrient blend of Ashwagandha and Turmeric extracts with magnesium and B-vitamins — made for an active lifestyle.' },
  { icon: Activity, title: 'Reduces Tiredness & Fatigue', text: 'Magnesium, Vitamin B6, B12 and Folic Acid contribute to the reduction of tiredness and fatigue.' },
  { icon: ShieldCheck, title: 'Supports the Immune System', text: 'Vitamin B6, B12 and Folic Acid contribute to the normal function of the immune system.' },
  { icon: HeartPulse, title: 'Muscle & Nervous System', text: 'Magnesium contributes to normal muscle function, and magnesium and Vitamin B6 contribute to the normal functioning of the nervous system.' },
  { icon: Brain, title: 'Everyday Balance', text: 'Vitamin B6, B12 and Folic Acid contribute to normal psychological function as part of a balanced diet.' },
  { icon: Leaf, title: 'Clean, Vegan Formula', text: '60 vegetarian capsules, allergen-free, GMP & ISO certified, with no artificial fillers.' },
];

// EU/GB Nutrition & Health Claims Register — permitted, substantiated claims only.
const RESEARCH_AREAS = [
  { nutrient: 'Magnesium', claims: ['Normal muscle function', 'Normal functioning of the nervous system', 'Reduction of tiredness and fatigue', 'Normal energy-yielding metabolism', 'Electrolyte balance'] },
  { nutrient: 'Vitamin B6', claims: ['Normal function of the immune system', 'Reduction of tiredness and fatigue', 'Normal functioning of the nervous system', 'Normal psychological function'] },
  { nutrient: 'Vitamin B12', claims: ['Reduction of tiredness and fatigue', 'Normal energy-yielding metabolism', 'Normal function of the immune system', 'Normal red blood cell formation'] },
  { nutrient: 'Folic Acid (B9)', claims: ['Normal function of the immune system', 'Reduction of tiredness and fatigue', 'Normal amino acid synthesis', 'Normal psychological function'] },
];

const PRICING = [
  { label: 'Single Bottle', qty: '1 bottle', price: '£35.99' },
  { label: '2 Bottles', qty: '£33.50 / bottle', price: '£67.00' },
  { label: '3 Bottles · Best Value', qty: '£32.50 / bottle', price: '£97.50', highlight: 'best' },
  { label: '4+ Bottles', qty: '£32.50 / bottle', price: 'same rate' },
];

export default function SominOn() {
  const [selected, setSelected] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [product, setProduct] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [email, setEmail] = useState('');
  const [joining, setJoining] = useState(false);
  const [joined, setJoined] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    axios.get(`${API}/products/${SOMIN_ID}`).then(r => setProduct(r.data)).catch(() => {});
  }, []);

  const productImages = product ? [...new Set([product.image, ...(product.images || [])].filter(Boolean))] : [];
  const GALLERY = productImages.length ? productImages : DEFAULT_GALLERY;
  const productName = product?.name || 'Somin-On™ Powered by BCM-95®';
  const altFor = (i) => `${productName} — product photo ${i + 1}`;
  const buyable = !!(product && product.available_for_purchase === true && product.status !== 'coming_soon');

  const handleBuy = () => {
    navigate('/checkout', { state: { productId: SOMIN_ID, quantity, product, couponCode: '' } });
  };

  const handleJoin = async (e) => {
    e.preventDefault();
    if (!email.trim()) { toast.error('Please enter your email.'); return; }
    setJoining(true);
    try {
      const r = await axios.post(`${API}/waitlist`, { email: email.trim(), product_id: SOMIN_ID, product_name: 'Somin-On™ Powered by BCM-95®' });
      setJoined(true);
      toast.success(r.data?.message || "You're on the list!");
    } catch (err) {
      toast.error('Could not sign you up. Please try again.');
    } finally {
      setJoining(false);
    }
  };

  return (
    <div className="min-h-screen py-24 md:py-32 bg-white">
      <SEO
        title="Somin-On™ Powered by BCM-95® | Ashwagandha & Turmeric | IIY Phytoventra"
        description="Somin-On™ Powered by BCM-95® — Ashwagandha & Turmeric Extract with magnesium and B-vitamins. Advanced nutritional support for an active lifestyle. Coming soon from IIY Phytoventra."
        keywords="ashwagandha turmeric supplement UK, ashwagandha BCM-95 UK, turmeric ashwagandha capsules, calm & everyday balance support, stress & calm support, IIY Phytoventra"
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'Product',
          name: 'Somin-On™ Powered by BCM-95®',
          category: 'Stress & Calm Support',
          description: 'Ashwagandha & Turmeric Extract (BCM-95®) with magnesium and B-vitamins — nutritional support as part of an active, balanced lifestyle. Coming soon.',
          brand: { '@type': 'Brand', name: 'IIY Phytoventra' },
          image: 'https://iiypv.com/og-image.jpg',
          url: 'https://iiypv.com/somin-on',
          offers: {
            '@type': 'Offer',
            priceCurrency: 'GBP',
            availability: 'https://schema.org/PreOrder',
            url: 'https://iiypv.com/somin-on',
            seller: { '@type': 'Organization', name: 'IIY Phytoventra' },
          },
        }}
      />
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        <button
          onClick={() => navigate('/products')}
          className="flex items-center gap-2 text-muted-foreground hover:text-primary mb-8 transition-colors"
          data-testid="somin-back-button"
        >
          <ArrowLeft className="w-5 h-5" />
          Back to Products
        </button>

        {/* ===== Hero: gallery + intro + pricing ===== */}
        <div className="grid lg:grid-cols-2 gap-12 items-start">
          <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} className="lg:sticky lg:top-24 self-start">
            <div className="relative bg-gradient-to-br from-emerald-100/60 to-white rounded-3xl p-6 mb-4">
              {!buyable && (
                <div className="absolute top-5 right-5 z-10 bg-amber-500 text-black text-[10px] font-bold tracking-[0.25em] uppercase px-3 py-1.5 rounded-full shadow-lg" data-testid="somin-coming-soon-badge">
                  Coming Soon
                </div>
              )}
              <img src={GALLERY[selected] || GALLERY[0]} alt={altFor(selected)} onClick={() => setLightboxOpen(true)} className="w-full aspect-square object-contain rounded-2xl cursor-zoom-in" data-testid="somin-main-image" />
            </div>
            <div className="grid grid-cols-5 gap-2">
              {GALLERY.map((img, i) => (
                <button
                  key={img}
                  onClick={() => setSelected(i)}
                  className={`bg-white rounded-xl p-1.5 border-2 transition-all aspect-square overflow-hidden ${selected === i ? 'border-emerald-600 shadow-lg' : 'border-border hover:border-emerald-400'}`}
                  data-testid={`somin-thumb-${i}`}
                >
                  <img src={img} alt={altFor(i)} className="w-full h-full object-cover rounded-lg" />
                </button>
              ))}
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="space-y-6">
            <div>
              <span className="text-sm font-medium tracking-widest uppercase text-emerald-700 mb-2 block">
                Powered by BCM-95®
              </span>
              <h1 className="text-4xl md:text-5xl font-bold mb-4 font-heading text-emerald-950" data-testid="somin-title">
                Somin-On™ Powered by BCM-95®
              </h1>
              <p className="text-sm text-muted-foreground">(60 Vegetarian Capsules · 2-a-day · Made for the UK)</p>
              <p className="text-lg text-muted-foreground leading-relaxed mt-3">
                Ashwagandha &amp; Turmeric Extract with BCM-95® curcumin, magnesium and B-vitamins — an advanced nutritional-support food supplement made for an active lifestyle.
              </p>
            </div>

            {/* Pricing chart */}
            <div className="bg-white border-2 border-emerald-700/20 rounded-2xl p-6" data-testid="somin-pricing-chart">
              <div className="flex items-center gap-2 mb-4">
                <Tag className="w-4 h-4 text-emerald-700" />
                <h2 className="text-lg font-bold text-emerald-950">Launch Pricing</h2>
              </div>
              <div className="space-y-2.5">
                {PRICING.map((row) => (
                  <div
                    key={row.label}
                    className={`flex items-center justify-between rounded-xl px-4 py-3 border ${row.highlight === 'welcome' ? 'bg-emerald-50 border-emerald-200' : row.highlight === 'best' ? 'bg-amber-50 border-amber-200' : 'bg-stone-50 border-stone-200'}`}
                  >
                    <div>
                      <p className="text-sm font-bold text-emerald-950">{row.label}</p>
                      <p className="text-xs text-muted-foreground">{row.qty}</p>
                    </div>
                    <p className="text-xl font-bold text-emerald-800">{row.price}</p>
                  </div>
                ))}
              </div>
              <p className="text-[11px] text-muted-foreground leading-snug pt-3">
                All prices are all-inclusive of UK VAT (20%) and standard delivery. 4 or more bottles are charged at the 3-bottle per-bottle rate.
              </p>

              {buyable ? (
                <div data-testid="somin-buy-block">
                  <div className="flex items-center gap-4 my-4">
                    <span className="text-sm font-medium">Quantity:</span>
                    <div className="flex items-center border-2 border-border rounded-xl overflow-hidden">
                      <button onClick={() => setQuantity(Math.max(1, quantity - 1))} className="px-4 py-2 hover:bg-gray-100" data-testid="somin-qty-decrease"><Minus className="w-4 h-4" /></button>
                      <input type="number" min="1" max="300" value={quantity} onChange={(e) => setQuantity(Math.min(300, Math.max(1, parseInt(e.target.value) || 1)))} className="w-16 text-center py-2 font-semibold border-x-2 border-border focus:outline-none" data-testid="somin-qty-input" />
                      <button onClick={() => setQuantity(Math.min(300, quantity + 1))} className="px-4 py-2 hover:bg-gray-100" data-testid="somin-qty-increase"><Plus className="w-4 h-4" /></button>
                    </div>
                  </div>
                  <button onClick={handleBuy} className="w-full inline-flex items-center justify-center gap-2 bg-emerald-700 hover:bg-emerald-800 text-white font-bold py-4 px-6 rounded-xl transition-colors" data-testid="somin-buy-btn">
                    <ShoppingBag className="w-4 h-4" /> Buy Now
                  </button>
                </div>
              ) : (
                <div className="mt-5" data-testid="somin-notify-block">
                  <button disabled className="w-full bg-gray-100 text-gray-500 font-semibold py-4 px-6 rounded-xl cursor-not-allowed" data-testid="somin-coming-soon-btn">
                    Coming Soon
                  </button>
                  {joined ? (
                    <div className="mt-3 flex items-center gap-2.5 px-4 py-3.5 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-900 text-sm font-medium" data-testid="somin-notify-success">
                      <CheckCircle className="w-5 h-5 text-emerald-600" /> You&apos;re on the list — we&apos;ll email you at launch.
                    </div>
                  ) : (
                    <form onSubmit={handleJoin} className="mt-3" data-testid="somin-notify-form">
                      <p className="text-[13px] font-semibold text-emerald-950 mb-2 flex items-center gap-2">
                        <BellRing className="w-4 h-4 text-emerald-700" /> Notify me when it launches
                      </p>
                      <div className="flex gap-2">
                        <input
                          type="email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          placeholder="you@example.com"
                          className="flex-1 border-2 border-border rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-emerald-500"
                          data-testid="somin-notify-email"
                          required
                        />
                        <button type="submit" disabled={joining} className="bg-amber-400 hover:bg-amber-300 text-emerald-950 font-bold px-5 rounded-xl transition-colors disabled:opacity-60" data-testid="somin-notify-submit">
                          {joining ? '…' : 'Notify Me'}
                        </button>
                      </div>
                    </form>
                  )}
                </div>
              )}

              <a
                href="mailto:sales@iiypv.com?subject=Somin-On%20Wholesale%20Enquiry"
                className="mt-3 w-full inline-flex items-center justify-center gap-2 border-2 border-emerald-700 text-emerald-800 hover:bg-emerald-50 font-semibold py-3.5 px-6 rounded-xl transition-colors"
                data-testid="somin-wholesale-enquiry"
              >
                <Mail className="w-4 h-4" />
                Wholesale enquiry (4+ bottles)
              </a>
            </div>

            {/* Trust chips */}
            <div className="flex flex-wrap gap-2.5" data-testid="somin-trust-chips">
              {['60 Vegan Capsules', 'GMP & ISO Certified', 'Allergen-Free', 'Made for the UK'].map((c) => (
                <span key={c} className="inline-flex items-center gap-2 bg-emerald-50 border border-emerald-100 rounded-full px-4 py-2 text-sm font-medium text-emerald-900">
                  <CheckCircle className="w-4 h-4 text-emerald-600" />{c}
                </span>
              ))}
            </div>
          </motion.div>
        </div>

        {/* ===== Product Specifications & Key Benefits ===== */}
        <section className="mt-20 md:mt-28" data-testid="somin-specs-section">
          <div className="text-center mb-12">
            <span className="text-sm font-medium tracking-widest uppercase text-emerald-700 mb-3 block">Product Specifications</span>
            <h2 className="text-3xl md:text-4xl font-bold font-heading text-emerald-950">Specifications &amp; Key Benefits</h2>
          </div>
          <div className="grid lg:grid-cols-2 gap-8">
            {/* Supplement facts */}
            <div className="bg-stone-50 rounded-3xl p-8 border border-stone-200">
              <h3 className="text-lg font-bold mb-1 text-emerald-950">Supplement Facts</h3>
              <p className="text-xs text-muted-foreground mb-4">Amounts per capsule · 60 capsules per bottle (NLT = not less than)</p>
              <div className="divide-y divide-stone-200">
                <div className="flex justify-between py-2 text-[11px] uppercase tracking-wide font-bold text-stone-500">
                  <span>Per Capsule</span><span>Amount</span><span>%NRV*</span>
                </div>
                {SUPPLEMENT_FACTS.map((f) => (
                  <div key={f.name} className="flex justify-between items-center py-2.5 text-sm gap-3">
                    <span className="text-emerald-950 flex-1">{f.name}</span>
                    <span className="font-semibold text-stone-700 w-24 text-right">{f.amount}</span>
                    <span className="text-stone-500 w-12 text-right">{f.nrv}</span>
                  </div>
                ))}
              </div>
              <p className="text-[11px] text-muted-foreground mt-3">*%NRV = percentage of the Nutrient Reference Value. † = NRV not established.</p>
              <p className="text-[11px] text-muted-foreground mt-2">Excipients Q.S.: Microcrystalline Cellulose, Silicon Dioxide, Vegetable Cellulose (capsule shell).</p>
              {product?.research_pdf && (
                <div className="mt-5 pt-4 border-t border-stone-200" data-testid="somin-research-reference">
                  <h4 className="text-sm font-bold text-emerald-950 mb-1">Research &amp; references</h4>
                  <p className="text-[12px] text-muted-foreground leading-snug mb-2">
                    The Somin-On™ ashwagandha extract has been the subject of published, peer-reviewed scientific research. The paper is provided below for information and transparency.
                  </p>
                  <a href={product.research_pdf} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-sm font-semibold text-emerald-700 hover:text-emerald-900 underline underline-offset-2" data-testid="somin-research-download">
                    <FileText className="w-4 h-4" /> Download the published paper (PDF)
                  </a>
                  <p className="text-[10px] text-muted-foreground leading-snug mt-2">
                    Provided as scientific literature about the ingredient; it is not a claim about this product. Food supplements are not a substitute for a varied, balanced diet and a healthy lifestyle.
                  </p>
                </div>
              )}
            </div>

            {/* Key benefits */}
            <div className="grid sm:grid-cols-2 gap-4">
              {KEY_BENEFITS.map((b, i) => (
                <div key={b.title} className="bg-white rounded-2xl p-5 border border-border/60 shadow-soft" data-testid={`somin-benefit-${i}`}>
                  <div className="w-11 h-11 rounded-xl bg-emerald-600/10 flex items-center justify-center mb-3">
                    <b.icon className="w-5 h-5 text-emerald-700" />
                  </div>
                  <h4 className="font-semibold text-emerald-950 mb-1.5 text-sm">{b.title}</h4>
                  <p className="text-xs text-muted-foreground leading-relaxed">{b.text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ===== Why the price is fair value ===== */}
        <section className="mt-20 md:mt-28 bg-gradient-to-br from-emerald-50 to-white rounded-3xl p-8 md:p-12 border border-emerald-100" data-testid="somin-fair-value-section">
          <div className="max-w-3xl">
            <span className="text-sm font-medium tracking-widest uppercase text-emerald-700 mb-3 block">Why This Price Is Fair Value</span>
            <h2 className="text-3xl md:text-4xl font-bold font-heading text-emerald-950 mb-6">Six actives. One capsule. One honest price.</h2>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-1" />
                <span className="text-muted-foreground"><strong className="text-emerald-950">A genuine multi-nutrient formula.</strong> Somin-On combines two branded extracts (Ashwagandha and BCM-95® Turmeric) with magnesium bisglycinate and three B-vitamins — actives that would cost far more bought separately.</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-1" />
                <span className="text-muted-foreground"><strong className="text-emerald-950">Branded, traceable ingredients.</strong> BCM-95® and Somin-on™ are registered trademarks of Arjuna Natural, manufactured under GMP &amp; ISO-certified standards with full crop-to-product traceability.</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-1" />
                <span className="text-muted-foreground"><strong className="text-emerald-950">All-inclusive pricing.</strong> Every price already includes UK VAT and free standard delivery — no surprises at checkout.</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-1" />
                <span className="text-muted-foreground"><strong className="text-emerald-950">Direct from the brand.</strong> Buying direct from IIY Phytoventra means no marketplace mark-ups — the saving stays with you.</span>
              </li>
            </ul>
          </div>
        </section>

        {/* ===== The science behind our bottle ===== */}
        <section className="mt-20 md:mt-28" data-testid="somin-science-section">
          <div className="grid lg:grid-cols-2 gap-10 items-center">
            <div className="relative">
              <div className="aspect-[4/3] rounded-3xl overflow-hidden shadow-2xl ring-1 ring-emerald-900/10">
                <img src="/somin-on-lifestyle.jpg" alt="Somin-On with Ashwagandha and Turmeric roots" className="w-full h-full object-cover" data-testid="somin-science-image" />
              </div>
            </div>
            <div>
              <span className="text-sm font-medium tracking-widest uppercase text-emerald-700 mb-3 block flex items-center gap-2">
                <Beaker className="w-4 h-4" /> The Science Behind Our Bottle
              </span>
              <h2 className="text-3xl md:text-4xl font-bold font-heading text-emerald-950 mb-5">Thoughtfully formulated, cleanly made.</h2>
              <p className="text-lg text-muted-foreground mb-5 leading-relaxed">
                Somin-On is built around <strong className="text-emerald-950">BCM-95®</strong> — the same clinically researched, highly bioavailable turmeric extract behind our flagship product — paired with a standardised Ashwagandha extract and a considered blend of magnesium and B-vitamins.
              </p>
              <ul className="space-y-3">
                <li className="flex items-start gap-3"><FlaskConical className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-1" /><span className="text-muted-foreground">Standardised, branded extracts for batch-to-batch consistency.</span></li>
                <li className="flex items-start gap-3"><Leaf className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-1" /><span className="text-muted-foreground">Magnesium as bisglycinate — a gentle, well-absorbed form.</span></li>
                <li className="flex items-start gap-3"><ShieldCheck className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-1" /><span className="text-muted-foreground">Manufactured under GMP &amp; ISO-certified standards.</span></li>
              </ul>
            </div>
          </div>
        </section>

        {/* ===== Research Across Multiple Areas of Health ===== */}
        <section className="mt-20 md:mt-28" data-testid="somin-research-section">
          <div className="text-center mb-12">
            <span className="text-sm font-medium tracking-widest uppercase text-emerald-700 mb-3 block">Research Across Multiple Areas of Health</span>
            <h2 className="text-3xl md:text-4xl font-bold font-heading text-emerald-950">Nutrients with authorised health claims</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto mt-4">
              Somin-On&apos;s added vitamins and minerals carry health claims authorised on the GB Nutrition &amp; Health Claims Register — the evidence-based statements permitted in the UK.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {RESEARCH_AREAS.map((r, i) => (
              <div key={r.nutrient} className="bg-white rounded-2xl p-6 border border-border/60 shadow-soft" data-testid={`somin-research-${i}`}>
                <h3 className="text-lg font-bold text-emerald-800 mb-4">{r.nutrient}</h3>
                <ul className="space-y-2.5">
                  {r.claims.map((c) => (
                    <li key={c} className="flex items-start gap-2 text-sm text-muted-foreground">
                      <CheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />{c}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          <p className="text-xs text-muted-foreground text-center max-w-3xl mx-auto mt-8">
            Food supplements should not be used as a substitute for a varied and balanced diet and a healthy lifestyle. Ashwagandha and Turmeric are included for an active-living routine; no medicinal claims are made for these botanicals.
          </p>
        </section>

        {/* ===== Closing CTA ===== */}
        <section className="mt-20 md:mt-28 text-center bg-emerald-900 text-white rounded-3xl p-10 md:p-14" data-testid="somin-cta-section">
          <h2 className="text-3xl md:text-4xl font-bold font-heading mb-4">Somin-On™ is launching soon.</h2>
          <p className="text-emerald-100/90 mb-8 max-w-xl mx-auto">
            Be among the first to try our advanced nutritional-support formula. For wholesale or trade enquiries, get in touch with our team.
          </p>
          <a
            href="mailto:sales@iiypv.com?subject=Somin-On%20Enquiry"
            className="inline-flex items-center gap-2 bg-amber-400 hover:bg-amber-300 text-emerald-950 font-bold rounded-full px-8 py-4 transition-colors"
            data-testid="somin-cta-enquiry"
          >
            <Mail className="w-4 h-4" /> Contact Us
          </a>
        </section>
      </div>
      <ImageLightbox open={lightboxOpen} images={GALLERY} index={selected} altFor={altFor} onClose={() => setLightboxOpen(false)} onIndexChange={setSelected} />
    </div>
  );
}
